import React, { useState, useEffect } from "react";
import { 
  fetchSalesReport, 
  fetchAcademicYears,
  fetchProductWiseReport,
  fetchProductSizeWiseReport,
  fetchClassWiseReport
} from "../../actions/product";
import { utils, writeFile } from "xlsx";
import { Table, Button, Form, Card, Spinner, Tabs, Tab } from "react-bootstrap";
import { useSelector } from "react-redux";
import { fetchClassesList } from "../../actions/student";
import Select from "react-select";

// Utility to format date as YYYY-MM-DD for input fields
const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

// Utility to format date as DD-MM-YYYY for display/export
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const getStartOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const Reports = () => {
  const { role, permissions } = useSelector((state) => state.auth.user || {});
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [activeTab, setActiveTab] = useState("sales");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' or 'desc'
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({
    startDate: formatDateForInput(getStartOfMonth()),
    endDate: formatDateForInput(new Date()),
    academicYear: "",
    className: []
  });

  const hasPermission = (permission) => {
    if (role === "superadmin") return true;
    return permissions?.[permission] === true;
  };

  const canViewSales = hasPermission("reports_view");
  const canViewProductWise = hasPermission("reports_product_wise_view");
  const canViewProductSizeWise = hasPermission("reports_product_size_wise_view");
  const canViewClassWise = hasPermission("reports_class_wise_view");

  useEffect(() => {
    loadAcademicYears();
    loadClassesList();
  }, []);

  const loadClassesList = async () => {
    const list = await fetchClassesList();
    if (list && Array.isArray(list)) {
      setClasses(list);
    }
  };

  // Dynamically set the initial tab based on permissions
  useEffect(() => {
    if (canViewSales) {
      setActiveTab("sales");
    } else if (canViewProductWise) {
      setActiveTab("product-wise");
    } else if (canViewProductSizeWise) {
      setActiveTab("product-size-wise");
    } else if (canViewClassWise) {
      setActiveTab("class-wise");
    }
  }, [role, permissions, canViewSales, canViewProductWise, canViewProductSizeWise, canViewClassWise]);

  const loadAcademicYears = async () => {
    const years = await fetchAcademicYears();
    if (years && Array.isArray(years)) {
      setAcademicYears(years);
      const current = years.find(y => y.is_current);
      if (current) {
        setFilters(prev => ({ ...prev, academicYear: current.year }));
      }
    }
  };

  const handleFetchReport = async (tab = activeTab) => {
    setLoading(true);
    try {
      let data = [];
      if (tab === "sales" && canViewSales) {
        data = await fetchSalesReport(
          filters.startDate,
          filters.endDate,
          filters.academicYear
        );
      } else if (tab === "product-wise" && canViewProductWise) {
        data = await fetchProductWiseReport(
          filters.startDate,
          filters.endDate,
          filters.academicYear
        );
      } else if (tab === "product-size-wise" && canViewProductSizeWise) {
        data = await fetchProductSizeWiseReport(
          filters.startDate,
          filters.endDate,
          filters.academicYear
        );
      } else if (tab === "class-wise" && canViewClassWise) {
        data = await fetchClassWiseReport(
          filters.startDate,
          filters.endDate,
          filters.academicYear,
          filters.className
        );
      }
      setReportData(data || []);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setReportData([]);
    setSortField("");
    setSortOrder("asc");
    // Fetch automatically on tab switch
    if (filters.startDate && filters.endDate) {
      handleFetchReport(key);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortedData = () => {
    if (!sortField) return reportData;

    return [...reportData].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";

      // Date sorting
      if (sortField === "date") {
        const dateA = new Date(valA);
        const dateB = new Date(valB);
        const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
        const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
        return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
      }

      // Numeric sorting
      if (typeof valA === "number" && typeof valB === "number") {
        return sortOrder === "asc" ? valA - valB : valB - valA;
      }

      // String sorting (case-insensitive)
      const stringA = String(valA).toLowerCase();
      const stringB = String(valB).toLowerCase();

      if (stringA < stringB) return sortOrder === "asc" ? -1 : 1;
      if (stringA > stringB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  const exportToExcel = () => {
    const sortedData = getSortedData();
    if (sortedData.length === 0) return;

    let dataToExport = [];
    let fileName = "";

    if (activeTab === "sales" && canViewSales) {
      dataToExport = sortedData.map(item => ({
        "Date": formatDateForDisplay(item.date),
        "Order ID": item.orderId,
        "USID": item.usid,
        "Student Name": item.studentName,
        "Qty": item.qty,
        "Taxable Value": item.taxableValue,
        "IGST": item.igst,
        "CGST": item.cgst,
        "SGST": item.sgst,
        "Total Invoice Value": item.totalInvoiceValue,
        "Delivery": item.delivery,
        "Final Invoice Value": item.finalAmount
      }));
      fileName = `Sales_Report_${filters.startDate}_to_${filters.endDate}.xlsx`;
    } else if (activeTab === "product-wise" && canViewProductWise) {
      dataToExport = sortedData.map(item => ({
        "Product Code": item.productCode,
        "Product Name": item.productName,
        "Total Qty Sold": item.totalQuantity
      }));
      fileName = `Product_Wise_Sales_Report_${filters.startDate}_to_${filters.endDate}.xlsx`;
    } else if (activeTab === "product-size-wise" && canViewProductSizeWise) {
      dataToExport = sortedData.map(item => ({
        "Product Code": item.productCode,
        "Product Name": item.productName,
        "Size": item.size,
        "Total Qty Sold": item.totalQuantity
      }));
      fileName = `Product_and_Size_Wise_Sales_Report_${filters.startDate}_to_${filters.endDate}.xlsx`;
    } else if (activeTab === "class-wise" && canViewClassWise) {
      const productsMap = {};
      sortedData.forEach(item => {
        const key = `${item.productCode || ""}_${item.productName || ""}_${item.displayName || ""}`;
        if (!productsMap[key]) {
          productsMap[key] = {
            productCode: item.productCode || "",
            productName: item.productName || "",
            displayName: item.displayName || "",
            classesData: {},
            productTotalQty: 0
          };
        }
        const cls = item.class || "Unknown";
        if (!productsMap[key].classesData[cls]) {
          productsMap[key].classesData[cls] = [];
        }
        productsMap[key].classesData[cls].push({
          size: item.size || "",
          totalQuantity: item.totalQuantity || 0
        });
        productsMap[key].productTotalQty += (item.totalQuantity || 0);
      });

      const uniqueClasses = Array.from(
        new Set(sortedData.map(item => item.class || "Unknown"))
      );
      uniqueClasses.sort((a, b) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });

      const productsOrder = [];
      sortedData.forEach(item => {
        const key = `${item.productCode || ""}_${item.productName || ""}_${item.displayName || ""}`;
        if (!productsOrder.includes(key)) {
          productsOrder.push(key);
        }
      });

      const headerRow1 = ["Product Code", "Product Name", "Display Name", "Total Qty"];
      const headerRow2 = ["", "", "", ""];

      uniqueClasses.forEach(clsName => {
        const label = clsName.toLowerCase().includes("class") || clsName.toLowerCase().includes("grade") ? clsName : `Class ${clsName}`;
        headerRow1.push(label, "");
        headerRow2.push("Size", "Qty");
      });

      const dataRows = [];
      const merges = [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Product Code
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Product Name
        { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // Display Name
        { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }  // Total Qty
      ];

      uniqueClasses.forEach((clsName, i) => {
        merges.push({
          s: { r: 0, c: 4 + 2 * i },
          e: { r: 0, c: 4 + 2 * i + 1 }
        });
      });

      let startRowIdx = 2; // Data starts after the two header rows
      productsOrder.forEach(key => {
        const prod = productsMap[key];
        const maxRows = Math.max(
          1,
          ...uniqueClasses.map(clsName => (prod.classesData[clsName] || []).length)
        );

        for (let r = 0; r < maxRows; r++) {
          const rowArr = [];
          if (r === 0) {
            rowArr.push(prod.productCode, prod.productName, prod.displayName, prod.productTotalQty);
          } else {
            rowArr.push("", "", "", "");
          }

          uniqueClasses.forEach(clsName => {
            const items = prod.classesData[clsName] || [];
            const record = items[r];
            if (record) {
              rowArr.push(record.size, record.totalQuantity);
            } else {
              rowArr.push("-", "-");
            }
          });

          dataRows.push(rowArr);
        }

        if (maxRows > 1) {
          merges.push({ s: { r: startRowIdx, c: 0 }, e: { r: startRowIdx + maxRows - 1, c: 0 } });
          merges.push({ s: { r: startRowIdx, c: 1 }, e: { r: startRowIdx + maxRows - 1, c: 1 } });
          merges.push({ s: { r: startRowIdx, c: 2 }, e: { r: startRowIdx + maxRows - 1, c: 2 } });
          merges.push({ s: { r: startRowIdx, c: 3 }, e: { r: startRowIdx + maxRows - 1, c: 3 } });
        }
        startRowIdx += maxRows;
      });

      const ws = utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);
      ws['!merges'] = merges;
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Product-Class-Size-Wise");
      fileName = `Class_Wise_Sales_Report_${filters.startDate}_to_${filters.endDate}.xlsx`;
      writeFile(wb, fileName);
      return;
    }

    const ws = utils.json_to_sheet(dataToExport);
    const wb = utils.book_new();
    utils.book_append_sheet(
      wb, 
      ws, 
      activeTab === "sales" 
        ? "Sales Report" 
        : activeTab === "product-wise" 
        ? "Product-Wise" 
        : activeTab === "product-size-wise"
        ? "Product-Size-Wise"
        : "Product-Class-Size-Wise"
    );
    writeFile(wb, fileName);
  };

  const SortableHeader = ({ field, label }) => {
    const isSorted = sortField === field;
    let iconClass = "bi bi-arrow-down-up ms-2 text-muted";
    if (isSorted) {
      iconClass = sortOrder === "asc" ? "bi bi-arrow-up ms-2 text-primary" : "bi bi-arrow-down ms-2 text-primary";
    }

    return (
      <th 
        className="small fw-bold text-nowrap align-middle" 
        onClick={() => handleSort(field)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <span>{label}</span>
          <i className={iconClass} style={{ fontSize: "0.85rem" }}></i>
        </div>
      </th>
    );
  };

  const renderTableHeader = () => {
    if (activeTab === "sales") {
      return (
        <tr>
          <SortableHeader field="date" label="Date" />
          <SortableHeader field="orderId" label="Order ID" />
          <SortableHeader field="usid" label="USID" />
          <SortableHeader field="studentName" label="Student Name" />
          <SortableHeader field="qty" label="Qty" />
          <SortableHeader field="taxableValue" label="Taxable Value" />
          <SortableHeader field="igst" label="IGST" />
          <SortableHeader field="cgst" label="CGST" />
          <SortableHeader field="sgst" label="SGST" />
          <SortableHeader field="totalInvoiceValue" label="Total Invoice Value" />
          <SortableHeader field="delivery" label="Delivery" />
          <SortableHeader field="finalAmount" label="Total" />
        </tr>
      );
    } else if (activeTab === "product-wise") {
      return (
        <tr>
          <SortableHeader field="productCode" label="Product Code" />
          <SortableHeader field="productName" label="Product Name" />
          <SortableHeader field="displayName" label="Display Name" />
          <SortableHeader field="totalQuantity" label="Total Qty Sold" />
        </tr>
      );
    } else if (activeTab === "product-size-wise") {
      return (
        <tr>
          <SortableHeader field="productCode" label="Product Code" />
          <SortableHeader field="productName" label="Product Name" />
          <SortableHeader field="displayName" label="Display Name" />
          <SortableHeader field="size" label="Size" />
          <SortableHeader field="totalQuantity" label="Total Qty Sold" />
        </tr>
      );
    } else if (activeTab === "class-wise") {
      return (
        <tr>
          <SortableHeader field="productCode" label="Product Code" />
          <SortableHeader field="productName" label="Product Name" />
          <SortableHeader field="displayName" label="Display Name" />
          <SortableHeader field="class" label="Class" />
          <SortableHeader field="size" label="Size" />
          <SortableHeader field="totalQuantity" label="Total Qty Sold" />
        </tr>
      );
    }
  };

  const renderTableBody = () => {
    const sortedData = getSortedData();
    if (sortedData.length === 0) {
      const colSpan = activeTab === "sales" ? 12 : activeTab === "product-wise" ? 4 : activeTab === "class-wise" ? 6 : 5;
      return (
        <tr>
          <td colSpan={colSpan} className="text-center py-4 text-muted">
            No records found for the selected period.
          </td>
        </tr>
      );
    }

    return sortedData.map((item, idx) => {
      if (activeTab === "sales") {
        return (
          <tr key={idx}>
            <td className="small">{formatDateForDisplay(item.date)}</td>
            <td className="small">{item.orderId}</td>
            <td className="small">{item.usid}</td>
            <td className="small">{item.studentName}</td>
            <td className="small">{item.qty}</td>
            <td className="small">{item.taxableValue}</td>
            <td className="small">{item.igst}</td>
            <td className="small">{item.cgst}</td>
            <td className="small">{item.sgst}</td>
            <td className="small fw-bold">{item.totalInvoiceValue}</td>
            <td className="small fw-bold">{item.delivery}</td>
            <td className="small fw-bold">{item.finalAmount}</td>
          </tr>
        );
      } else if (activeTab === "product-wise") {
        return (
          <tr key={idx}>
            <td className="small">{item.productCode}</td>
            <td className="small">{item.productName}</td>
            <td className="small">{item.displayName}</td>
            <td className="small fw-bold">{item.totalQuantity}</td>
          </tr>
        );
      } else if (activeTab === "product-size-wise") {
        return (
          <tr key={idx}>
            <td className="small">{item.productCode}</td>
            <td className="small">{item.productName}</td>
            <td className="small">{item.displayName}</td>
            <td className="small fw-semibold">{item.size}</td>
            <td className="small fw-bold">{item.totalQuantity}</td>
          </tr>
        );
      } else if (activeTab === "class-wise") {
        return (
          <tr key={idx}>
            <td className="small">{item.productCode}</td>
            <td className="small">{item.productName}</td>
            <td className="small">{item.displayName}</td>
            <td className="small fw-semibold">{item.class}</td>
            <td className="small fw-semibold">{item.size}</td>
            <td className="small fw-bold">{item.totalQuantity}</td>
          </tr>
        );
      }
      return null;
    });
  };

  const renderClassWiseTables = () => {
    const sortedData = getSortedData();
    const productsOrder = [];
    const productsMap = {};

    sortedData.forEach(item => {
      const key = `${item.productCode || ""}_${item.productName || ""}_${item.displayName || ""}`;
      if (!productsMap[key]) {
        productsMap[key] = {
          productCode: item.productCode || "",
          productName: item.productName || "",
          displayName: item.displayName || "",
          classesData: {},
          productTotalQty: 0
        };
        productsOrder.push(key);
      }
      const cls = item.class || "Unknown";
      if (!productsMap[key].classesData[cls]) {
        productsMap[key].classesData[cls] = [];
      }
      productsMap[key].classesData[cls].push({
        size: item.size || "",
        totalQuantity: item.totalQuantity || 0
      });
      productsMap[key].productTotalQty += (item.totalQuantity || 0);
    });

    const uniqueClasses = Array.from(
      new Set(sortedData.map(item => item.class || "Unknown"))
    );
    uniqueClasses.sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });

    const productsList = productsOrder.map(key => productsMap[key]);

    return (
      <div className="table-responsive">
        <Table hover bordered size="sm" className="align-middle">
          <thead className="bg-light text-center">
            <tr>
              <th rowSpan={2} className="small fw-bold align-middle text-start">
                <SortableHeader field="productCode" label="Product Code" />
              </th>
              <th rowSpan={2} className="small fw-bold align-middle text-start">
                <SortableHeader field="productName" label="Product Name" />
              </th>
              <th rowSpan={2} className="small fw-bold align-middle text-start">
                <SortableHeader field="displayName" label="Display Name" />
              </th>
              <th rowSpan={2} className="small fw-bold align-middle text-center" style={{ minWidth: "80px" }}>
                Total Qty
              </th>
              {uniqueClasses.map(clsName => {
                const label = clsName.toLowerCase().includes("class") || clsName.toLowerCase().includes("grade") ? clsName : `Class ${clsName}`;
                return (
                  <th key={clsName} colSpan={2} className="small fw-bold align-middle text-center text-nowrap">
                    {label}
                  </th>
                );
              })}
            </tr>
            <tr>
              {uniqueClasses.map(clsName => (
                <React.Fragment key={clsName}>
                  <th className="small fw-semibold text-center border-start-0" style={{ fontSize: "0.8rem", minWidth: "50px" }}>Size</th>
                  <th className="small fw-semibold text-center" style={{ fontSize: "0.8rem", minWidth: "50px" }}>Qty</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {productsList.map((prod, pIdx) => {
              const maxRows = Math.max(
                1,
                ...uniqueClasses.map(clsName => (prod.classesData[clsName] || []).length)
              );

              const rows = [];
              for (let r = 0; r < maxRows; r++) {
                rows.push(
                  <tr key={`${pIdx}_${r}`}>
                    {r === 0 && (
                      <>
                        <td rowSpan={maxRows} className="small align-middle fw-semibold bg-white">{prod.productCode}</td>
                        <td rowSpan={maxRows} className="small align-middle bg-white">{prod.productName}</td>
                        <td rowSpan={maxRows} className="small align-middle bg-white">{prod.displayName}</td>
                        <td rowSpan={maxRows} className="small align-middle text-center fw-bold bg-white">{prod.productTotalQty}</td>
                      </>
                    )}
                    {uniqueClasses.map(clsName => {
                      const items = prod.classesData[clsName] || [];
                      const record = items[r];
                      return (
                        <React.Fragment key={clsName}>
                          <td className="small text-center align-middle" style={{ minWidth: "50px" }}>
                            {record ? record.size : "-"}
                          </td>
                          <td className="small text-center align-middle fw-bold" style={{ minWidth: "50px" }}>
                            {record ? record.totalQuantity : "-"}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              }
              return rows;
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  if (!canViewSales && !canViewProductWise && !canViewProductSizeWise && !canViewClassWise) {
    return (
      <div className="container py-5 text-center">
        <Card className="shadow-sm border-0 p-5">
          <h4 className="text-danger fw-bold">Access Denied</h4>
          <p className="text-muted mb-0">You do not have permission to view any sales reports.</p>
        </Card>
      </div>
    );
  }

  const dateColSize = activeTab === "class-wise" ? "col-md-2" : "col-md-3";

  return (
    <div className="container-fluid py-4">
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white py-3">
          <h5 className="mb-0 fw-bold">Sales Reports</h5>
        </Card.Header>
        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="mb-4 custom-reports-tabs"
          >
            {canViewSales && <Tab eventKey="sales" title="General Sales Report" />}
            {canViewProductWise && <Tab eventKey="product-wise" title="Product-Wise Sales Report" />}
            {canViewProductSizeWise && <Tab eventKey="product-size-wise" title="Product & Size-Wise Sales Report" />}
            {canViewClassWise && <Tab eventKey="class-wise" title="Product, Class & Size-Wise Sales Report" />}
          </Tabs>
 
          <Form className="row g-3 align-items-end mb-4">
            <div className={dateColSize}>
              <Form.Label className="small fw-bold">From Date</Form.Label>
              <Form.Control 
                type="date" 
                size="sm"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />
            </div>
            <div className={dateColSize}>
              <Form.Label className="small fw-bold">To Date</Form.Label>
              <Form.Control 
                type="date" 
                size="sm"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />
            </div>
            <div className="col-md-2">
              <Form.Label className="small fw-bold">Academic Year</Form.Label>
              <Form.Select 
                size="sm"
                value={filters.academicYear}
                onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
              >
                <option value="">All</option>
                {academicYears.map(year => (
                  <option key={year.id} value={year.year}>{year.year}</option>
                ))}
              </Form.Select>
            </div>
            {activeTab === "class-wise" && (
              <div className="col-md-3">
                <Form.Label className="small fw-bold">Classes</Form.Label>
                <Select
                  isMulti
                  name="classes"
                  options={[{ value: "all", label: "All" }, ...classes.map(cls => ({ value: cls, label: cls }))]}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={
                    !filters.className || filters.className.length === 0
                      ? [{ value: "all", label: "All" }]
                      : filters.className.map(c => ({ value: c, label: c }))
                  }
                  onChange={(selectedOptions) => {
                    if (!selectedOptions || selectedOptions.length === 0) {
                      setFilters({ ...filters, className: [] });
                      return;
                    }
                    const values = selectedOptions.map(opt => opt.value);
                    const hadAll = !filters.className || filters.className.length === 0;
                    const selectedAll = values.includes("all");

                    if (selectedAll && !hadAll) {
                      setFilters({ ...filters, className: [] });
                    } else if (selectedAll && values.length > 1) {
                      setFilters({ ...filters, className: values.filter(v => v !== "all") });
                    } else {
                      setFilters({ ...filters, className: values.filter(v => v !== "all") });
                    }
                  }}
                  placeholder="Select classes..."
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: '31px',
                      height: 'auto',
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: '0 6px',
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: '0px',
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: '31px',
                    }),
                  }}
                />
              </div>
            )}
            <div className="col-md-4 d-flex gap-2">
              <Button 
                variant="primary" 
                size="sm" 
                onClick={() => handleFetchReport()}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Generate Report"}
              </Button>
              <Button 
                variant="success" 
                size="sm" 
                onClick={exportToExcel}
                disabled={reportData.length === 0 || loading}
              >
                Export to Excel
              </Button>
            </div>
          </Form>

          {activeTab === "class-wise" ? (
            loading ? (
              <div className="table-responsive">
                <Table hover bordered size="sm" className="align-middle">
                  <tbody>
                    <tr>
                      <td colSpan={10} className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            ) : reportData.length === 0 ? (
              <div className="table-responsive">
                <Table hover bordered size="sm" className="align-middle">
                  <tbody>
                    <tr>
                      <td colSpan={10} className="text-center py-4 text-muted">
                        No records found for the selected period.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            ) : (
              renderClassWiseTables()
            )
          ) : (
            <div className="table-responsive">
              <Table hover bordered size="sm" className="align-middle">
                <thead className="bg-light">
                  {renderTableHeader()}
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={activeTab === "sales" ? 12 : activeTab === "product-wise" ? 4 : activeTab === "class-wise" ? 6 : 5} className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                      </td>
                    </tr>
                  ) : (
                    renderTableBody()
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Reports;
